import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Card, Field, PageHeader } from "../components/common/UI";
import { Button } from "../components/ui/button";

const EMPTY = {
  measure_type_id: "", term_id: "", proposed_title: "", short_title: "", abstract: "", statement_of_purpose: "", background: "",
  author_legislator_id: "", principal_sponsor_id: "", co_sponsor_ids: [], originating_office_id: "", priority: "normal", committee_id: "",
  legal_basis: "", budgetary_implications: "", confidentiality: "internal", subjects: "", legal_references: "", whereas_clauses: "", enacting_clause: "", body: "",
};

export const Select = ({ testId, value, onChange, items, placeholder, labelKey = "name", valueKey = "id", required }) => (
  <select data-testid={testId} required={required} className="otms-input" value={value || ""} onChange={(e) => onChange(e.target.value)}>
    <option value="">{placeholder || "— Select —"}</option>
    {items.map((i) => <option key={i[valueKey]} value={i[valueKey]}>{i[labelKey]}</option>)}
  </select>
);

export default function MeasureCreate() {
  const nav = useNavigate();
  const [lk, setLk] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/lookups").then((r) => {
      setLk(r.data);
      const cur = r.data.terms.find((t) => t.is_current);
      setF((x) => ({ ...x, term_id: cur?.id || "" }));
    });
    api.get("/templates").then((r) => setTemplates(r.data.filter((t) => t.is_active)));
  }, []);
  const up = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const applyTemplate = (id) => {
    const t = templates.find((x) => x.id === id);
    if (t) setF((x) => ({ ...x, whereas_clauses: t.whereas_clauses || "", enacting_clause: t.enacting_clause || "", body: t.body || "" }));
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...f };
    Object.keys(payload).forEach((k) => payload[k] === "" && (payload[k] = null));
    payload.subjects = f.subjects.split(",").map((s) => s.trim()).filter(Boolean);
    payload.legal_references = f.legal_references.split("\n").map((s) => s.trim()).filter(Boolean);
    try {
      const { data } = await api.post("/measures", payload);
      toast.success(`Draft ${data.tracking_ref} created`);
      nav(`/measures/${data.id}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  if (!lk) return <div className="text-sm text-slate-500">Loading…</div>;
  return (
    <form onSubmit={submit} className="fade-up" data-testid="measure-create-form">
      <PageHeader eyebrow="Legislative Registry" title="New measure" subtitle="Creates a draft with a public tracking reference. Official numbers are assigned only by the configured workflow step."
        actions={<Button type="submit" disabled={busy} className="bg-sky-600 hover:bg-sky-700" data-testid="measure-create-submit">{busy ? "Saving…" : "Create draft"}</Button>} />
      <div className="grid gap-6 xl:grid-cols-3">
        <Card title="Identification" className="xl:col-span-2">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Measure type *"><Select required testId="input-measure-type" value={f.measure_type_id} onChange={up("measure_type_id")} items={lk.measure_types.filter((t) => t.is_active)} /></Field>
            <Field label="Legislative term *"><Select required testId="input-term" value={f.term_id} onChange={up("term_id")} items={lk.terms} /></Field>
            <Field label="Proposed title *" className="md:col-span-2"><textarea required minLength={5} rows={2} data-testid="input-proposed-title" className="otms-input" value={f.proposed_title} onChange={up("proposed_title")} /></Field>
            <Field label="Short title"><input data-testid="input-short-title" className="otms-input" value={f.short_title} onChange={up("short_title")} /></Field>
            <Field label="Subjects" hint="Comma-separated"><input data-testid="input-subjects" className="otms-input" value={f.subjects} onChange={up("subjects")} /></Field>
            <Field label="Abstract" className="md:col-span-2"><textarea rows={3} data-testid="input-abstract" className="otms-input" value={f.abstract} onChange={up("abstract")} /></Field>
            <Field label="Statement of purpose" className="md:col-span-2"><textarea rows={3} data-testid="input-purpose" className="otms-input" value={f.statement_of_purpose} onChange={up("statement_of_purpose")} /></Field>
            <Field label="Background and justification" className="md:col-span-2"><textarea rows={3} data-testid="input-background" className="otms-input" value={f.background} onChange={up("background")} /></Field>
          </div>
        </Card>
        <Card title="Authorship and handling">
          <div className="space-y-4">
            <Field label="Author"><Select testId="input-author" value={f.author_legislator_id} onChange={up("author_legislator_id")} items={lk.legislators} labelKey="full_name" /></Field>
            <Field label="Principal sponsor"><Select testId="input-principal-sponsor" value={f.principal_sponsor_id} onChange={up("principal_sponsor_id")} items={lk.legislators} labelKey="full_name" /></Field>
            <Field label="Co-sponsors" hint="Hold Ctrl/Cmd to select several">
              <select multiple data-testid="input-co-sponsors" className="otms-input h-24" value={f.co_sponsor_ids} onChange={(e) => up("co_sponsor_ids")([...e.target.selectedOptions].map((o) => o.value))}>
                {lk.legislators.map((l) => <option key={l.id} value={l.id}>{l.full_name}</option>)}
              </select>
            </Field>
            <Field label="Originating office"><Select testId="input-office" value={f.originating_office_id} onChange={up("originating_office_id")} items={lk.offices} /></Field>
            <Field label="Committee (proposed)"><Select testId="input-committee" value={f.committee_id} onChange={up("committee_id")} items={lk.committees} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Priority"><Select testId="input-priority" value={f.priority} onChange={up("priority")} items={["low", "normal", "high", "urgent"].map((p) => ({ id: p, name: p }))} /></Field>
              <Field label="Classification"><Select testId="input-confidentiality" value={f.confidentiality} onChange={up("confidentiality")} items={["public", "internal", "confidential", "restricted"].map((p) => ({ id: p, name: p }))} /></Field>
            </div>
          </div>
        </Card>
        <Card title="Draft text (version 1)" className="xl:col-span-2" actions={
          <select data-testid="input-template" className="otms-input h-8 w-56 text-xs" defaultValue="" onChange={(e) => applyTemplate(e.target.value)}>
            <option value="">Apply a template…</option>
            {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>}>
          <div className="space-y-4">
            <Field label="Whereas clauses"><textarea rows={4} data-testid="input-whereas" className="otms-input font-mono text-xs" value={f.whereas_clauses} onChange={up("whereas_clauses")} /></Field>
            <Field label="Enacting / resolving clause"><textarea rows={2} data-testid="input-enacting" className="otms-input font-mono text-xs" value={f.enacting_clause} onChange={up("enacting_clause")} /></Field>
            <Field label="Body (articles and sections)"><textarea rows={10} data-testid="input-body" className="otms-input font-mono text-xs" value={f.body} onChange={up("body")} /></Field>
          </div>
        </Card>
        <Card title="Legal and budgetary">
          <div className="space-y-4">
            <Field label="Legal basis"><textarea rows={3} data-testid="input-legal-basis" className="otms-input" value={f.legal_basis} onChange={up("legal_basis")} /></Field>
            <Field label="Legal references" hint="One per line"><textarea rows={3} data-testid="input-legal-refs" className="otms-input" value={f.legal_references} onChange={up("legal_references")} /></Field>
            <Field label="Budgetary implications"><textarea rows={3} data-testid="input-budget" className="otms-input" value={f.budgetary_implications} onChange={up("budgetary_implications")} /></Field>
          </div>
        </Card>
      </div>
    </form>
  );
}
